import { useQuery } from '@tanstack/react-query';
import { getCRMContactFollowUpDetail, getWhatsappTemplates } from '../api';
import { TemplatePhonePreview } from '../../components/TemplatePhonePreview';
import { Button, SideDrawer, TableChip } from '@/components';
import { getFollowUpStatusChipStyle } from '../utils/tasksAndFollowups';
import { formatInterval } from '../../utils/intervalUtils';

export function CRMFollowUpDetailModal({
  companyId,
  contactId,
  followUpId,
  onClose,
  readOnly = false,
  onPause,
  onResume,
  onStop,
  onReschedule,
  isPausing = false,
  isResuming = false,
  isStopping = false,
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'crm-contact-follow-up-detail',
      companyId,
      contactId,
      followUpId,
    ],
    queryFn: () =>
      getCRMContactFollowUpDetail({ companyId, contactId, followUpId }),
    enabled: !!companyId && !!contactId && !!followUpId,
  });

  const { data: templatesResponse, isLoading: isTemplatesLoading } = useQuery({
    queryKey: [
      'whatsapp-templates',
      companyId,
      data?.template_id,
      data?.template_name,
    ],
    queryFn: () => getWhatsappTemplates(companyId, { page_size: 200 }),
    enabled: !!companyId && !!data && data.message_type === 'template',
  });

  const templateList =
    templatesResponse?.results ||
    templatesResponse?.data?.results ||
    templatesResponse ||
    [];
  const templates = Array.isArray(templateList) ? templateList : [];
  const templateId = data?.template_id != null ? String(data.template_id) : '';
  const templateName = data?.template_name || '';
  const selectedTemplate = templates.find((t) => {
    const ids = [t.id, t.uuid, t.template_id, t.whatsapp_template_id]
      .filter((value) => value != null && value !== '')
      .map(String);
    if (templateId && ids.includes(templateId)) return true;
    return Boolean(
      templateName &&
      (t.name === templateName || t.template_name === templateName),
    );
  });

  let templateValues = {};
  if (data?.template_variables) {
    try {
      templateValues =
        typeof data.template_variables === 'string'
          ? JSON.parse(data.template_variables)
          : data.template_variables;
    } catch {
      // Ignore parse errors
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const hasActions =
    !isLoading &&
    !isError &&
    data &&
    !readOnly &&
    ((data.follow_up_type === 'interval' &&
      (data.status === 'active' ||
        data.status === 'running' ||
        data.status === 'scheduled' ||
        data.status === 'paused')) ||
      (data.follow_up_type === 'scheduled' &&
        (data.status === 'active' ||
          data.status === 'running' ||
          data.status === 'scheduled' ||
          data.status === 'stopped' ||
          data.status === 'failed')));

  return (
    <SideDrawer
      isOpen={!!followUpId}
      onClose={onClose}
      width="md"
      header={
        <div className="flex items-center justify-between w-full">
          <h2 className="text-xl font-bold text-[#1F2241]">
            Follow-up Details
          </h2>
          <div className="flex items-center gap-3">
            {!isLoading && !isError && data?.status && (
              <TableChip {...getFollowUpStatusChipStyle(data.status)}>
                {data.status}
              </TableChip>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      }
      bodyClassName="p-6"
      footer={
        hasActions ? (
          <div className="flex justify-end gap-3">
            {data.follow_up_type === 'interval' &&
              (data.status === 'active' ||
                data.status === 'running' ||
                data.status === 'scheduled') && (
                <Button
                  variant="secondary"
                  onClick={onPause}
                  disabled={isPausing}
                  className="h-10 px-4 text-sm"
                >
                  {isPausing ? 'Pausing...' : 'Pause'}
                </Button>
              )}
            {data.follow_up_type === 'interval' && data.status === 'paused' && (
              <Button
                variant="primary"
                onClick={onResume}
                disabled={isResuming}
                className="h-10 px-4 text-sm"
              >
                {isResuming ? 'Resuming...' : 'Resume'}
              </Button>
            )}
            {data.follow_up_type === 'scheduled' &&
              (data.status === 'active' ||
                data.status === 'running' ||
                data.status === 'scheduled') && (
                <Button
                  variant="secondary"
                  onClick={onStop}
                  disabled={isStopping}
                  className="h-10 px-4 text-sm text-red-500"
                >
                  {isStopping ? 'Stopping...' : 'Stop'}
                </Button>
              )}
            {data.follow_up_type === 'scheduled' &&
              (data.status === 'stopped' || data.status === 'failed') && (
                <Button
                  variant="primary"
                  onClick={onReschedule}
                  className="h-10 px-4 text-sm"
                >
                  Reschedule
                </Button>
              )}
          </div>
        ) : null
      }
    >
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : isError || !data ? (
        <p className="text-sm text-red-500 text-center py-4">
          Failed to load follow-up details.
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          <div>
            <h4 className="text-xs font-semibold text-[#868686] tracking-wider mb-2">
              Message Content
            </h4>
            <div className="p-4 rounded-[12px] bg-[#F5F5F5]">
              {data.channel === 'email' ? (
                <div className="flex flex-col gap-2">
                  {data.email_subject && (
                    <div className="text-sm font-semibold text-[#1F2241]">
                      Subject: {data.email_subject}
                    </div>
                  )}
                  <p className="text-sm text-[#1F2241] whitespace-pre-wrap break-words font-medium">
                    {data.standard_message_body || '-'}
                  </p>
                </div>
              ) : data.message_type === 'template' ? (
                <div className="flex flex-col gap-6 items-center w-full">
                  <div className="flex flex-col gap-1 w-full text-center">
                    <span className="text-sm font-semibold text-[#1F2241]">
                      Template: {data.template_name || '-'}
                    </span>
                    <span className="text-xs text-[#868686]">
                      ID: {data.template_id || '-'}
                    </span>
                  </div>

                  <div className="flex-shrink-0 mx-auto">
                    {isTemplatesLoading ? (
                      <div className="w-[260px] h-[300px] flex items-center justify-center bg-white border border-[#EBF4EA] rounded-[34px]">
                        <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                      </div>
                    ) : selectedTemplate ? (
                      <TemplatePhonePreview
                        template={selectedTemplate}
                        templateValues={templateValues}
                      />
                    ) : (
                      <div className="w-[260px] p-4 text-center text-sm text-[#868686] bg-white border border-[#EBF4EA] rounded-xl">
                        Template preview unavailable
                      </div>
                    )}
                  </div>

                  {Object.keys(templateValues).length > 0 && (
                    <div className="flex flex-col gap-2 bg-white rounded-lg p-4 border border-[#EBF4EA] w-full text-left">
                      <span className="text-xs font-semibold text-[#868686] uppercase tracking-wider mb-1">
                        Variables
                      </span>
                      <div className="grid grid-cols-2 gap-3">
                        {Object.entries(templateValues).map(([key, val]) => (
                          <div key={key} className="flex flex-col">
                            <span className="text-[10px] text-[#868686] truncate mb-0.5">
                              {key}
                            </span>
                            <span
                              className="text-sm font-medium text-[#1F2241] break-words"
                              title={String(val)}
                            >
                              {String(val)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-[#1F2241] whitespace-pre-wrap break-words font-medium">
                  {data.standard_message_body || '-'}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
              <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                Follow-up Type
              </div>
              <p className="text-sm font-medium text-[#1F2241] capitalize">
                {data.follow_up_type || '-'}
              </p>
            </div>

            <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
              <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                Message Type
              </div>
              <p className="text-sm font-medium text-[#1F2241] capitalize">
                {data.message_type || '-'}
              </p>
            </div>

            {data.follow_up_type === 'scheduled' ? (
              <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5] sm:col-span-2">
                <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                  Scheduled For
                </div>
                <p className="text-sm font-medium text-[#1F2241]">
                  {formatDate(data.scheduled_date_time)}
                </p>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                  <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                    Interval
                  </div>
                  <p className="text-sm font-medium text-[#1F2241]">
                    {formatInterval(data.interval_value, data.interval_unit)}
                  </p>
                </div>
                <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                  <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                    Stop On Reply
                  </div>
                  <p className="text-sm font-medium text-[#1F2241]">
                    {data.stop_on_reply ? 'Yes' : 'No'}
                  </p>
                </div>
                <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                  <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                    Start Date
                  </div>
                  <p className="text-sm font-medium text-[#1F2241]">
                    {formatDate(data.start_date)}
                  </p>
                </div>
                <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
                  <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                    End Date
                  </div>
                  <p className="text-sm font-medium text-[#1F2241]">
                    {formatDate(data.end_date)}
                  </p>
                </div>
              </>
            )}

            <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
              <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                Attempts
              </div>
              <p className="text-sm font-medium text-[#1F2241]">
                {data.completed_attempts || 0} /{' '}
                {data.max_attempts > 0 ? data.max_attempts : 'Unlimited'}
              </p>
            </div>

            <div className="flex flex-col gap-1 p-4 rounded-[12px] bg-[#F5F5F5]">
              <div className="text-xs font-semibold text-[#868686] tracking-wider mb-1">
                Next Execution
              </div>
              <p className="text-sm font-medium text-[#1F2241]">
                {formatDate(data.next_execution_time)}
              </p>
            </div>
          </div>
        </div>
      )}
    </SideDrawer>
  );
}
